"use client";

import { UserEvents, trackEvent } from "@/analytics";

import { Comedian } from "@/data/comedians";
import Image from "next/image";
import LandingPageTracker from "./LandingPageTracker";
import Link from "next/link";
import React from "react";
import Runtime from "@/components/common/Runtime";
import { SpecialRow } from "@/data/specials";
import UnlockPill from "@/components/common/UnlockPill";
import { getCDNImageURL } from "@/data/images";
import { getStoredShortLinkContext } from "../providers/ShortLinkProvider";

interface Props {
  special: SpecialRow;
  comedian: Comedian;
}

export default function ContentBanner({ special, comedian }: Props) {
  const shortLinkCtx = getStoredShortLinkContext();
  const { shortCode } = shortLinkCtx || {};

  const posterImg = special.preview_img
    ? getCDNImageURL(special.preview_img)
    : null;

  const mobilePosterImg = special.small_preview_img
    ? getCDNImageURL(special.small_preview_img)
    : null;

  // the new code after the redesign
  return (
    <div className="relative">
      <LandingPageTracker comedian={comedian} special={special} />
      <Link
        href={`/${comedian.slug}/${special.slug}`}
        onClick={() => {
          trackEvent(UserEvents.CLICKED_CONTENT_BANNER, {
            ComedianId: comedian.id,
            SpecialId: special.id,
            Comedian: comedian.display_name,
            "Special Title": special.title,
          });
        }}
        className="flex flex-col w-full p-4 bg-black rounded-[18px]"
      >
        {/* desktop version */}
        {posterImg && (
          <Image
            className="hidden object-cover w-full max-w-full bg-black rounded-lg md:block aspect-video"
            src={posterImg}
            alt="Comedy Special"
            width={1000}
            height={563}
            priority={true}
          />
        )}
        {/* mobile version */}
        {mobilePosterImg && (
          <Image
            className="object-cover max-w-full overflow-hidden bg-black rounded-lg md:hidden aspect-square self-center"
            src={mobilePosterImg}
            alt="Comedy Special"
            width={500}
            height={500}
            priority={true}
          />
        )}

        <div className="flex flex-col mt-3 sm:mt-4 md:grid md:grid-cols-3 md:gap-x-2 md:p-2">
          <div className="md:col-span-2">
            <h2 className="truncate uppercase font-anton font-medium text-2xl xs:text-3xl sm:text-4xl text-white [text-shadow:_2px_4px_2px_#000] leading-none md:leading-[1.1]">
              {special.title}
            </h2>
            <div className="flex flex-row gap-4 mt-2 text-sm text-white">
              <span className="flex items-center">
                {special.requires_subscription ? "Podcast" : "Video"}
              </span>
              {!special.livestream && (
                <Runtime
                  runtime={special.runtime}
                  className="flex items-center"
                />
              )}
            </div>
          </div>
          <UnlockPill
            className="flex flex-row justify-center items-center w-full p-2 md:mt-0 mt-3 sm:mt-4 text-white btn font-sans text-[16px] sm:text-[18px] font-bold py-5 sm:py-6 rounded-lg leading-none"
            special={special}
            comedianId={comedian.id}
          />
        </div>
      </Link>
    </div>
  );
}
